/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
  FiCheckCircle,
  FiGlobe,
  FiClock,
  FiWifi,
  FiInfo,
  FiArrowRight,
} from 'react-icons/fi';
import {
  formatPrice,
  formatDataCapacity,
  formatValidity,
  parseCoverages,
  derivePlanTitle,
  derivePlanSubtitle,
  isUnlimitedPlan,
} from '../../utils/esimUtils';
import './EsimPlanCard.scss';

function EsimPlanCard({ plan, selected, onSelect, onViewDetails }) {
  const title = derivePlanTitle(plan);
  const subtitle = derivePlanSubtitle(plan);
  const coverages = parseCoverages(plan?.coverages);
  const previewCoverages = coverages.slice(0, 6);
  const moreCount = Math.max(0, coverages.length - previewCoverages.length);
  const currency = plan?.currency || 'INR';
  const total = Number(plan?.totalPrice || plan?.netPrice || 0);
  const slashed = Number(plan?.slashedNetPrice || 0);
  const showSlashed = slashed > total;

  return (
    <div
      className={`esim-plan-card ${selected ? 'esim-plan-card--selected' : ''}`}
      onClick={() => onSelect && onSelect(plan)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onSelect) onSelect(plan);
      }}
    >
      {selected && (
        <span className="esim-plan-card__selected-badge">
          <FiCheckCircle />
          Selected
        </span>
      )}

      <header className="esim-plan-card__head">
        <div className="esim-plan-card__title-block">
          <h3 className="esim-plan-card__title">{title}</h3>
          {subtitle && <p className="esim-plan-card__subtitle">{subtitle}</p>}
        </div>
        <div className="esim-plan-card__price-block">
          {showSlashed && (
            <span className="esim-plan-card__price-slashed">
              {formatPrice(slashed, currency) || `${currency} ${slashed}`}
            </span>
          )}
          <span className="esim-plan-card__price">
            {formatPrice(total, currency) || `${currency} ${total}`}
          </span>
          <span className="esim-plan-card__price-sub">incl. taxes</span>
        </div>
      </header>

      <div className="esim-plan-card__chips">
        <span className="esim-plan-card__chip">
          <FiWifi />
          <strong>{formatDataCapacity(plan)}</strong>
        </span>
        <span className="esim-plan-card__chip">
          <FiClock />
          <strong>{formatValidity(plan) || 'Flexible validity'}</strong>
        </span>
        {isUnlimitedPlan(plan) && (
          <span className="esim-plan-card__chip esim-plan-card__chip--accent">
            Unlimited
          </span>
        )}
        {plan?.isRechargeable && (
          <span className="esim-plan-card__chip esim-plan-card__chip--muted">
            Rechargeable
          </span>
        )}
      </div>

      <div className="esim-plan-card__coverages">
        <span className="esim-plan-card__coverages-label">
          <FiGlobe />
          Covers
        </span>
        <div className="esim-plan-card__coverages-list">
          {previewCoverages.length === 0 && (
            <span className="esim-plan-card__coverage">
              {plan?.country_covered || 'Selected destination'}
            </span>
          )}
          {previewCoverages.map((c) => (
            <span key={c} className="esim-plan-card__coverage">
              {c}
            </span>
          ))}
          {moreCount > 0 && (
            <span
              className="esim-plan-card__coverage esim-plan-card__coverage--more"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (onViewDetails) onViewDetails(plan);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  if (onViewDetails) onViewDetails(plan);
                }
              }}
            >
              +{moreCount} more
            </span>
          )}
        </div>
      </div>

      <footer className="esim-plan-card__foot">
        <button
          type="button"
          className="esim-plan-card__details-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (onViewDetails) onViewDetails(plan);
          }}
        >
          <FiInfo />
          View plan details
        </button>
        <button
          type="button"
          className="esim-plan-card__select-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (onSelect) onSelect(plan);
          }}
        >
          {selected ? 'Selected' : 'Select Plan'}
          <FiArrowRight />
        </button>
      </footer>
    </div>
  );
}

EsimPlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  onViewDetails: PropTypes.func,
};

EsimPlanCard.defaultProps = {
  selected: false,
  onSelect: null,
  onViewDetails: null,
};

export default EsimPlanCard;
